import { Input } from '@app/components/various/Fields/Input';
import { Calendar } from '@app/components/various/Icons';
import { DATE_STRING_FORMAT } from '@app/constants/dates';
import { cn } from '@app/utils/cn';
import { handleSpaceAndEnter } from '@app/utils/handleEnterKey';
import { isDefined, isNull } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { DatePickerNs, Input as InputType } from '@typings';
import React from 'react';

import { useDatePickerSelectionContext } from '../context';

import { DatePickerInputs } from './DatePickerInputs';

interface Props {
  dataTestId?: string;
  disabled: boolean;
  invalid: boolean;
  placeholder: string;
  formatter?: (value: DatePickerNs.Value) => string;
  onClick: () => void;
  size?: InputType.Size;
  describedById?: string;
  onVisibilityChange: (value: boolean) => void;
  isCalendarVisible: boolean;
}

export const DatePickerTrigger = React.forwardRef<HTMLInputElement, Props>((props, ref) => {
  const { dataTestId, disabled, invalid, placeholder, formatter, onClick, size, describedById, isCalendarVisible, onVisibilityChange } =
    props;
  const { startDate, endDate, isRangeSelection } = useDatePickerSelectionContext();

  const selectionLabel = React.useMemo(() => {
    if (isDefined(formatter)) {
      return formatter(isRangeSelection ? [startDate, endDate] : startDate);
    }

    if (!isRangeSelection) {
      return startDate?.format(DATE_STRING_FORMAT) ?? '';
    }

    if (isNull(startDate) && isNull(endDate)) {
      return '';
    }

    return `${startDate?.format(DATE_STRING_FORMAT)} - ${endDate?.format(DATE_STRING_FORMAT) ?? ''}`;
  }, [endDate, formatter, isRangeSelection, startDate]);

  return (
    <div className="relative">
      <Input
        ref={ref}
        data-testid={dataTestId}
        value={selectionLabel}
        readOnly
        className={cn('cursor-pointer pr-9', {
          'pointer-events-none invisible absolute': isCalendarVisible,
        })}
        inputClassName={cn({ 'tabular-nums !-tracking-[0.06rem]': !isEmpty(selectionLabel) })}
        placeholder={placeholder}
        isDisabled={disabled}
        isInvalid={invalid}
        onClick={onClick}
        onKeyDown={handleSpaceAndEnter(onClick)}
        size={size}
        describedById={describedById}
      />
      {isCalendarVisible && <DatePickerInputs dataTestId={dataTestId} placeholder={placeholder} onVisibilityChange={onVisibilityChange} />}
      <Calendar className="absolute right-2 top-0 size-5 translate-y-1/2 text-neutral-40" />
    </div>
  );
});
